import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ButtonV2 } from '@utilities';
import { submitNarrativeFeedback } from '@api/narratives';

import FeedbackFooter from '../../../FeedbackFooter';

import {
  negativeNarrativeFeedbackOptions,
  positiveNarrativeFeedbackOptions,
} from './utilities/helpers';

import styles from './_index.module.scss';

const TopicCardFooter = ({
  handleToggleVisibility,
  initialFeedback,
  isDraftCard = false,
  isExcluded,
  isLoading,
  isPreviewActive = false,
  isPreviewCard = false,
  topicControls = null,
  topicId,
}) => {
  const buttonText = () => {
    if (isPreviewCard) {
      return isPreviewActive ? 'Close' : 'Preview';
    }
    return isExcluded ? 'Include' : 'Exclude';
  };

  const footerMessage = () => {
    if (isPreviewCard) {
      return isPreviewActive ? (
        <span className={styles['topic-card-footer-message']}>
          This is a preview, not an actual analysis. Reach out to your account team for more
          information.
        </span>
      ) : (
        <span className={styles['topic-card-footer-message']}>
          <strong>Excluded</strong> This question is answered utilizing data outside of your current
          subscription.
        </span>
      );
    }
  };

  const renderDefaultButton = () => (
    <ButtonV2
      className={classNames(styles['topic-card-footer-exclude-button'], {
        [styles['is-preview']]: isPreviewCard,
        [styles['is-preview-active']]: isPreviewActive,
      })}
      disabled={isLoading}
      onClick={handleToggleVisibility}
    >
      {buttonText()}
    </ButtonV2>
  );

  const renderControlButtons = () => {
    if (!topicControls) return null;

    const { primaryAction = null, secondaryActions = [] } = topicControls;
    const resolvedPrimary = primaryAction?.label
      ? {
          disabled: primaryAction?.disabled,
          label: primaryAction.label,
          onClick: primaryAction?.onClick,
          tooltip: primaryAction?.tooltip,
        }
      : null;

    const resolvedSecondary = Array.isArray(secondaryActions)
      ? secondaryActions.filter((action) => action?.label)
      : [];

    return (
      <>
        {resolvedPrimary && (
          <ButtonV2
            disabled={resolvedPrimary.disabled}
            onClick={resolvedPrimary.onClick}
            tooltip={resolvedPrimary.tooltip}
          >
            {resolvedPrimary.label}
          </ButtonV2>
        )}
        {resolvedSecondary.map((action) => (
          <ButtonV2
            disabled={action?.disabled}
            key={action.label}
            onClick={action?.onClick}
            tooltip={action?.tooltip}
          >
            {action.label}
          </ButtonV2>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className={styles['topic-card-footer-actions']}>
        {topicControls ? renderControlButtons() : renderDefaultButton()}
        {footerMessage()}
      </div>
      {!isDraftCard && (
        <FeedbackFooter
          disabled={isLoading || isPreviewCard}
          feedbackId={topicId}
          feedbackLabel="Was this slide helpful?"
          initialFeedback={initialFeedback}
          negativeFeedbackOptions={negativeNarrativeFeedbackOptions}
          onSubmit={submitNarrativeFeedback}
          positiveFeedbackOptions={positiveNarrativeFeedbackOptions}
        />
      )}
    </div>
  );
};

TopicCardFooter.propTypes = {
  handleToggleVisibility: PropTypes.func.isRequired,
  initialFeedback: PropTypes.shape({
    like: PropTypes.bool,
    reason: PropTypes.string,
    reasonExplanation: PropTypes.string,
  }),
  isDraftCard: PropTypes.bool,
  isExcluded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPreviewActive: PropTypes.bool,
  isPreviewCard: PropTypes.bool,
  topicControls: PropTypes.shape({
    primaryAction: PropTypes.shape({
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      tooltip: PropTypes.node,
    }),
    secondaryActions: PropTypes.arrayOf(
      PropTypes.shape({
        disabled: PropTypes.bool,
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        tooltip: PropTypes.node,
      })
    ),
  }),
  topicId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TopicCardFooter;
